import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert, ActivityIndicator, Modal, ScrollView } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import QRCode from 'react-native-qrcode-svg';
import { qrCodeService, QRPaymentData } from '../services/qrCodeService';
import { paymentRequestService } from '../services/paymentRequestService';
import PaymentMethodBrandIcon from '../components/PaymentMethodBrandIcon';

interface QRCodePaymentScreenProps {
  onBack: () => void;
  businessId: string;
  businessName: string;
}

const QRCodePaymentScreen: React.FC<QRCodePaymentScreenProps> = ({
  onBack,
  businessId,
  businessName,
}) => {
  const [qrPayment, setQrPayment] = useState<QRPaymentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    name: '',
    email: '',
  });
  const [qrConfig, setQrConfig] = useState({
    size: 200,
    color: '#000000',
    backgroundColor: '#FFFFFF',
    logoSize: 40
  });
  const [qrFormData, setQrFormData] = useState({
    serviceName: 'Haircut & Styling',
    amount: '45.00',
    description: 'Professional haircut and styling service',
    expirationHours: 24
  });

  const availablePaymentMethods = [
    { id: 'apple_pay', name: 'Apple Pay' },
    { id: 'google_pay', name: 'Google Pay' },
    { id: 'venmo', name: 'Venmo' },
    { id: 'cash_app', name: 'Cash App' },
    { id: 'zelle', name: 'Zelle' },
    { id: 'credit_card', name: 'Credit Card' },
  ];

  useEffect(() => {
    loadQRConfig();
  }, []);

  const loadQRConfig = async () => {
    try {
      const config = await qrCodeService.getQRConfig();
      setQrConfig(config);
    } catch (error) {
      console.error('Error loading QR config:', error);
    }
  };

  const generateQRCode = async () => {
    setLoading(true);
    try {
      const amount = parseFloat(qrFormData.amount);
      if (isNaN(amount) || amount <= 0) {
        Alert.alert('Invalid Amount', 'Please enter a valid amount greater than 0');
        setLoading(false);
        return;
      }

      const qrData = await qrCodeService.generatePaymentQR({
        businessId,
        businessName,
        serviceName: qrFormData.serviceName,
        amount: amount,
        description: qrFormData.description,
        paymentMethods: ['apple_pay', 'google_pay', 'credit_card'],
        expirationHours: qrFormData.expirationHours
      });

      setQrPayment(qrData);
    } catch (error) {
      console.error('Error generating QR code:', error);
      Alert.alert('Error', 'Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodSelect = (methodId: string) => {
    setSelectedPaymentMethod(methodId);
    
    if (['apple_pay', 'google_pay', 'venmo', 'cash_app', 'zelle'].includes(methodId)) {
      handleProcessPayment(methodId);
    } else {
      setShowPaymentModal(true);
    }
  };

  const handleProcessPayment = async (methodId?: string) => {
    if (!qrPayment) return;

    const method = methodId || selectedPaymentMethod;
    if (!method) return;

    setLoading(true);

    try {
      const success = await qrCodeService.processQRPayment(qrPayment.id, {
        customerId: 'customer_' + Date.now(),
        customerName: 'QR Customer',
        paymentMethod: method,
        paymentData: methodId ? undefined : paymentData
      });

      if (success) {
        Alert.alert(
          'Payment Successful!',
          `Your payment of $${qrPayment.amount} has been processed successfully.`,
          [
            {
              text: 'OK',
              onPress: () => {
                setQrPayment(null);
                setShowPaymentModal(false);
              }
            }
          ]
        );
      } else {
        Alert.alert('Payment Failed', 'Please try again or contact support.');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      Alert.alert('Payment Error', 'An error occurred while processing your payment.');
    } finally {
      setLoading(false);
      setShowPaymentModal(false);
    }
  };

  const formatCardNumber = (text: string) => {
    const cleaned = text.replace(/\D/g, '');
    const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
    return formatted.slice(0, 19);
  };

  const formatExpiryDate = (text: string) => {
    const cleaned = text.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  const getTimeRemaining = () => {
    if (!qrPayment) return '';
    
    const now = new Date();
    const expires = new Date(qrPayment.expiresAt);
    const diff = expires.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else {
      return `${minutes}m remaining`;
    }
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>QR Code Payment</Text>
      </View>

      <ScrollView style={styles.scrollContainer}>
        {!qrPayment ? (
          <View style={styles.generateContainer}>
          <Text style={styles.generateTitle}>Generate QR Code for Payment</Text>
          <Text style={styles.generateDescription}>
            Create a QR code that customers can scan to pay for services
          </Text>

          {/* Service Details Form */}
          <View style={styles.formContainer}>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Service Name</Text>
              <AppTextInput
                style={styles.input}
                value={qrFormData.serviceName}
                onChangeText={(text) => setQrFormData({...qrFormData, serviceName: text})}
                placeholder="Enter service name"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Amount ($)</Text>
              <AppTextInput
                style={styles.input}
                value={qrFormData.amount}
                onChangeText={(text) => setQrFormData({...qrFormData, amount: text})}
                placeholder="0.00"
                keyboardType="numeric"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Description</Text>
              <AppTextInput
                style={[styles.input, styles.textArea]}
                value={qrFormData.description}
                onChangeText={(text) => setQrFormData({...qrFormData, description: text})}
                placeholder="Enter service description"
                multiline
                numberOfLines={3}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Expiration (Hours)</Text>
              <AppTextInput
                style={styles.input}
                value={qrFormData.expirationHours.toString()}
                onChangeText={(text) => setQrFormData({...qrFormData, expirationHours: parseInt(text) || 24})}
                placeholder="24"
                keyboardType="numeric"
              />
            </View>
          </View>

          <TouchableOpacity
            style={[styles.generateButton, loading && styles.disabledButton]}
            onPress={generateQRCode}
            disabled={loading}
          >
            {loading ? (
              <ActivityIndicator color="white" />
            ) : (
              <Text style={styles.generateButtonText}>Generate QR Code</Text>
            )}
          </TouchableOpacity>
          </View>
        ) : (
        <View style={styles.qrContainer}>
          {/* QR Code Display */}
          <View style={styles.qrCodeContainer}>
            <View style={[styles.qrCode, { width: qrConfig.size, height: qrConfig.size }]}>
              <QRCode
                value={JSON.stringify({
                  type: 'payment',
                  businessId: qrPayment.businessId,
                  businessName: qrPayment.businessName,
                  amount: qrPayment.amount,
                  serviceName: qrPayment.serviceName,
                  qrId: qrPayment.id,
                  timestamp: qrPayment.createdAt
                })}
                size={qrConfig.size - 20}
                color={qrConfig.color}
                backgroundColor={qrConfig.backgroundColor}
              />
            </View>
            <Text style={styles.qrCodeSubtext}>Scan to Pay</Text>
          </View>

          {/* Payment Details */}
          <View style={styles.paymentDetailsCard}>
            <Text style={styles.businessName}>{qrPayment.businessName}</Text>
            <Text style={styles.serviceName}>{qrPayment.serviceName}</Text>
            <Text style={styles.amount}>${qrPayment.amount}</Text>
            <Text style={styles.description}>{qrPayment.description}</Text>
            <Text style={styles.expiration}>⏰ {getTimeRemaining()}</Text>
          </View>

          {/* Payment Methods */}
          <View style={styles.paymentMethodsContainer}>
            <Text style={styles.paymentMethodsTitle}>Payment Methods</Text>
            <View style={styles.paymentMethodsGrid}>
              {availablePaymentMethods.map(method => (
                <TouchableOpacity
                  key={method.id}
                  style={styles.paymentMethodCard}
                  onPress={() => handlePaymentMethodSelect(method.id)}
                >
                  <View style={styles.paymentMethodIconWrap}>
                    <PaymentMethodBrandIcon methodId={method.id} size={36} />
                  </View>
                  <Text style={styles.paymentMethodName}>{method.name}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Actions */}
          <View style={styles.actionsContainer}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => setQrPayment(null)}
            >
              <Text style={styles.actionButtonText}>Generate New QR Code</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Payment Form Modal */}
      <Modal
        visible={showPaymentModal}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Payment Details</Text>
              <TouchableOpacity onPress={() => setShowPaymentModal(false)}>
                <Text style={styles.modalCloseButton}>✕</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.paymentForm}>
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Card Number</Text>
                <AppTextInput
                  style={styles.input}
                  value={paymentData.cardNumber}
                  onChangeText={(text) => setPaymentData({...paymentData, cardNumber: formatCardNumber(text)})}
                  placeholder="1234 5678 9012 3456"
                  keyboardType="numeric"
                  maxLength={19}
                />
              </View>

              <View style={styles.row}>
                <View style={[styles.inputGroup, styles.halfWidth]}>
                  <Text style={styles.inputLabel}>Expiry Date</Text>
                  <AppTextInput
                    style={styles.input}
                    value={paymentData.expiryDate}
                    onChangeText={(text) => setPaymentData({...paymentData, expiryDate: formatExpiryDate(text)})}
                    placeholder="MM/YY"
                    keyboardType="numeric"
                    maxLength={5}
                  />
                </View>
                <View style={[styles.inputGroup, styles.halfWidth]}>
                  <Text style={styles.inputLabel}>CVV</Text>
                  <AppTextInput
                    style={styles.input}
                    value={paymentData.cvv}
                    onChangeText={(text) => setPaymentData({...paymentData, cvv: text.replace(/\D/g, '').slice(0, 4)})}
                    placeholder="123"
                    keyboardType="numeric"
                    maxLength={4}
                    secureTextEntry
                  />
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Cardholder Name</Text>
                <AppTextInput
                  style={styles.input}
                  value={paymentData.name}
                  onChangeText={(text) => setPaymentData({...paymentData, name: text})}
                  placeholder="Enter cardholder name"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Email</Text>
                <AppTextInput
                  style={styles.input}
                  value={paymentData.email}
                  onChangeText={(text) => setPaymentData({...paymentData, email: text})}
                  placeholder="john@example.com"
                  keyboardType="email-address"
                />
              </View>

              <TouchableOpacity
                style={styles.payButton}
                onPress={() => handleProcessPayment()}
                disabled={loading}
              >
                <Text style={styles.payButtonText}>
                  Pay ${qrPayment?.amount}
                </Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5E9',
  },
  backButton: {
    marginRight: 15,
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  scrollContainer: {
    flex: 1,
  },
  generateContainer: {
    padding: 20,
    padding: 20,
  },
  formContainer: {
    marginVertical: 20,
  },
  inputGroup: {
    marginBottom: 15,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E1E5E9',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: 'white',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  generateTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 10,
    textAlign: 'center',
  },
  generateDescription: {
    fontSize: 16,
    color: '#8E8E93',
    marginBottom: 30,
    textAlign: 'center',
    lineHeight: 22,
  },
  generateButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 15,
    paddingHorizontal: 30,
    borderRadius: 8,
    alignItems: 'center',
  },
  disabledButton: {
    backgroundColor: '#8E8E93',
  },
  generateButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  qrContainer: {
    padding: 20,
  },
  qrCodeContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  qrCode: {
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    borderColor: '#E1E5E9',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  qrCodeText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  qrCodeSubtext: {
    fontSize: 12,
    color: '#8E8E93',
    marginTop: 5,
  },
  paymentDetailsCard: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 12,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  businessName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 5,
  },
  serviceName: {
    fontSize: 16,
    color: '#8E8E93',
    marginBottom: 10,
  },
  amount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#34C759',
    marginBottom: 10,
  },
  description: {
    fontSize: 14,
    color: '#8E8E93',
    marginBottom: 10,
  },
  expiration: {
    fontSize: 14,
    color: '#FF9500',
    fontWeight: '600',
  },
  paymentMethodsContainer: {
    marginBottom: 20,
  },
  paymentMethodsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 15,
  },
  paymentMethodsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  paymentMethodCard: {
    width: '48%',
    padding: 15,
    backgroundColor: 'white',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E1E5E9',
    alignItems: 'center',
    marginBottom: 10,
  },
  paymentMethodIconWrap: {
    width: 44,
    height: 44,
    marginBottom: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  paymentMethodName: {
    fontSize: 12,
    color: '#1C1C1E',
    textAlign: 'center',
  },
  actionsContainer: {
    marginTop: 20,
  },
  actionButton: {
    backgroundColor: '#8E8E93',
    paddingVertical: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  actionButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    width: '90%',
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5E9',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  modalCloseButton: {
    fontSize: 20,
    color: '#8E8E93',
  },
  paymentForm: {
    padding: 20,
  },
  inputGroup: {
    marginBottom: 15,
  },
  halfWidth: {
    width: '48%',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E1E5E9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#1C1C1E',
    backgroundColor: 'white',
  },
  payButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 20,
  },
  payButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default QRCodePaymentScreen;

